/**
 * Auth Token Utility
 * 
 * This module provides utilities for getting the Firebase ID token
 * to include in API requests. This is needed because Safari's ITP
 * blocks third-party cookies (including SameSite=None session cookies)
 * when the frontend and backend are on different domains.
 */

import { auth } from './firebase';

let cachedToken: string | null = null;
let tokenExpiry: number = 0;

/**
 * Get the current Firebase ID token for the authenticated user.
 * Returns null if no user is signed in.
 * 
 * The token is cached for performance but refreshed before expiry.
 */
export async function getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;

    if (!user) {
        cachedToken = null;
        tokenExpiry = 0;
        return null;
    }

    // Check if we have a cached token that's still valid (with 5 min buffer)
    const now = Date.now();
    if (cachedToken && tokenExpiry > now + 5 * 60 * 1000) {
        return cachedToken;
    }

    try {
        // Force refresh if token is close to expiry
        const forceRefresh = tokenExpiry > 0 && tokenExpiry < now + 10 * 60 * 1000;
        const token = await user.getIdToken(forceRefresh);

        // Firebase tokens are valid for 1 hour, cache with that assumption
        cachedToken = token;
        tokenExpiry = now + 55 * 60 * 1000; // 55 minutes (5 min buffer)

        return token;
    } catch (error) {
        console.error('[AuthToken] Failed to get ID token:', error);
        cachedToken = null;
        tokenExpiry = 0;
        return null;
    }
}

/**
 * Clear the cached token (call on logout)
 */
export function clearAuthToken(): void {
    cachedToken = null;
    tokenExpiry = 0;
}
